import React, { useEffect, useState } from 'react';
import { Card, Form, Button, Spinner, Row, Col, Alert, Table } from 'react-bootstrap';
import api from '../lib/api';

const tonePresets = {
  professional: `- Be concise and clear.\n- Avoid humor or filler.\n- Use formal business tone.`,
  friendly: `- Use casual, friendly tone.\n- Add emojis occasionally if appropriate.\n- Be encouraging.`,
  formal: `- Use polite and proper grammar.\n- Avoid contractions.\n- Use academic tone.`,
};

const MODELS = ['mistral', 'tinyllama', 'openai', 'gemini', 'groq'];
const USE_CASES = ['default', 'support', 'sales', 'developer'];
const TONES = ['custom', 'professional', 'friendly', 'formal'];

function AdminInstructionsCard() {
  const [instructions, setInstructions] = useState([]);
  const [current, setCurrent] = useState({
    model: 'mistral',
    use_case: 'default',
    tone: 'custom',
    content: '',
    updated_at: null,
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState('');
  const [question, setQuestion] = useState('What is your refund policy?');
  const [saveMsg, setSaveMsg] = useState('');
  const [saveErr, setSaveErr] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/chat-instructions/');
        const data = Array.isArray(res.data) ? res.data : [];
        setInstructions(data);
      } catch {
        setSaveErr('Unable to load instruction sets.');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    const found = instructions.find(
      (i) => i.model === current.model && i.use_case === current.use_case
    );
    if (found) {
      setCurrent({
        model: found.model,
        use_case: found.use_case,
        tone: found.tone || 'custom',
        content: found.content || '',
        updated_at: found.updated_at || null,
      });
    } else {
      setCurrent((prev) => ({
        ...prev,
        content: '',
        tone: 'custom',
        updated_at: null,
      }));
    }
  }, [instructions, current.model, current.use_case]);

  const handleSave = async () => {
    setSaving(true);
    setSaveMsg('');
    setSaveErr('');
    try {
      const res = await api.post('/chat-instructions/', {
        model: current.model,
        use_case: current.use_case,
        tone: current.tone,
        content: current.content,
      });

      const now = new Date().toISOString();
      const saved = {
        model: current.model,
        use_case: current.use_case,
        tone: current.tone,
        content: current.content,
        updated_at: now,
      };

      const idx = instructions.findIndex(
        (i) => i.model === current.model && i.use_case === current.use_case
      );
      if (idx >= 0) {
        const next = [...instructions];
        next[idx] = { ...instructions[idx], ...saved };
        setInstructions(next);
      } else {
        setInstructions((prev) => [...prev, { id: res?.data?.id, ...saved }]);
      }

      setCurrent((prev) => ({ ...prev, updated_at: now }));
      setSaveMsg(res?.data?.message || 'Saved');
      setTimeout(() => setSaveMsg(''), 1800);
    } catch (err) {
      setSaveErr(
        err?.response?.data?.error ||
          err?.response?.data?.detail ||
          err?.message ||
          'Error saving instructions'
      );
      setTimeout(() => setSaveErr(''), 3000);
    } finally {
      setSaving(false);
    }
  };

  const applyTonePreset = (tone) => {
    setCurrent((prev) => ({
      ...prev,
      tone,
      content: tonePresets[tone] || '',
    }));
  };

  const handlePreview = () => {
    const fullPrompt = `
You are an assistant trained to respond like this:

Instructions from admin:
${current.content}

User question: ${question}
Answer:`.trim();
    setPreview(fullPrompt);
  };

  if (loading) return <Spinner animation="border" />;

  return (
    <Card className="shadow-sm p-3 mb-4">
      <h5 className="mb-3">🧠 AI Instruction Manager</h5>

      {saveMsg ? <Alert variant="success" className="py-2">{saveMsg}</Alert> : null}
      {saveErr ? <Alert variant="danger" className="py-2">{saveErr}</Alert> : null}

      {/* Read-only table of all instruction sets */}
      <Card className="p-3 mb-3">
        <h6 className="mb-2">All Instruction Sets (Read-only)</h6>
        <div className="small text-muted mb-2">
          Loaded from <code>GET /chat-instructions/</code>.
        </div>
        <Table size="sm" responsive hover>
          <thead>
            <tr>
              <th>ID</th>
              <th>Model</th>
              <th>Use Case</th>
              <th>Tone</th>
              <th>Updated</th>
              <th>Content</th>
            </tr>
          </thead>
          <tbody>
            {instructions.length === 0 ? (
              <tr><td colSpan={6} className="text-center">No instruction sets</td></tr>
            ) : (
              instructions.map((i) => (
                <tr key={`${i.id ?? `${i.model}-${i.use_case}`}`}>
                  <td>{i.id ?? '-'}</td>
                  <td>{i.model}</td>
                  <td>{i.use_case}</td>
                  <td>{i.tone || 'custom'}</td>
                  <td>{i.updated_at ? new Date(i.updated_at).toLocaleString() : 'N/A'}</td>
                  <td style={{ whiteSpace: 'pre-wrap' }}>{i.content}</td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </Card>

      {/* Editor */}
      <Row className="mb-3">
        <Col md={4}>
          <Form.Group>
            <Form.Label>Model</Form.Label>
            <Form.Select
              value={current.model}
              onChange={(e) => setCurrent((prev) => ({ ...prev, model: e.target.value }))}
              disabled={saving}
            >
              {MODELS.map((m) => <option key={m}>{m}</option>)}
            </Form.Select>
          </Form.Group>
        </Col>

        <Col md={4}>
          <Form.Group>
            <Form.Label>Use Case</Form.Label>
            <Form.Select
              value={current.use_case}
              onChange={(e) => setCurrent((prev) => ({ ...prev, use_case: e.target.value }))}
              disabled={saving}
            >
              {USE_CASES.map((u) => <option key={u}>{u}</option>)}
            </Form.Select>
          </Form.Group>
        </Col>

        <Col md={4}>
          <Form.Group>
            <Form.Label>Tone</Form.Label>
            <Form.Select
              value={current.tone}
              onChange={(e) => {
                const tone = e.target.value;
                if (tone !== 'custom') applyTonePreset(tone);
                else setCurrent((prev) => ({ ...prev, tone }));
              }}
              disabled={saving}
            >
              {TONES.map((t) => <option key={t}>{t}</option>)}
            </Form.Select>
          </Form.Group>
        </Col>
      </Row>

      <Form.Group className="mb-2">
        <Form.Label>Instruction Text</Form.Label>
        <Form.Control
          as="textarea"
          rows={6}
          value={current.content}
          onChange={(e) => setCurrent((prev) => ({ ...prev, content: e.target.value }))}
          disabled={saving}
        />
        <div className="small text-muted mt-1">
          Last updated: {current.updated_at ? new Date(current.updated_at).toLocaleString() : 'N/A'}
        </div>
      </Form.Group>

      <div className="d-flex gap-2">
        <Button variant="primary" onClick={handleSave} disabled={saving} style={{ cursor: 'pointer' }}>
          {saving ? (<><Spinner size="sm" animation="border" className="me-2" />Saving…</>) : 'Save Instructions'}
        </Button>
        <Button variant="secondary" onClick={handlePreview} disabled={saving} style={{ cursor: 'pointer' }}>
          Preview
        </Button>
      </div>

      {preview && (
        <Card className="mt-3 p-3 bg-light">
          <h6 className="mb-2">🧪 Prompt Preview</h6>
          <Form.Group className="mb-2">
            <Form.Label>Sample Question</Form.Label>
            <Form.Control
              type="text"
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              disabled={saving}
            />
          </Form.Group>
          <div style={{ whiteSpace: 'pre-wrap', fontSize: '0.9rem', background: '#f9f9f9' }}>
            {preview}
          </div>
        </Card>
      )}
    </Card>
  );
}

export default AdminInstructionsCard;
